/*
  <complementary-products> — curated "Looks Cute With" carousel on the product page.

  Lazy-loads via IntersectionObserver: the section ships an empty shell on
  initial page load, then refetches itself via the Section Rendering API
  (intent=complementary) once the shell scrolls near the viewport. The
  fetched section's innerHTML is swapped in, then carousel + per-card
  interactions are bound.

  Per-card behavior:
  - Size buttons select a variant; the displayed price + ATC variant-id
    update accordingly. Disabled buttons are unselectable.
  - ATC posts to /cart/add.js, then opens window.CartDrawer (the theme's
    cart drawer — same flow as the main product form).

  Arrow nav uses native scrollTo() on the track with wrap-around behavior.
  Carousel auto-rotates and loops back to the first card after the last.
  No external slider library — keeping deps minimal.
*/

if (!customElements.get('complementary-products')) {
  customElements.define('complementary-products', class ComplementaryProducts extends HTMLElement {
    connectedCallback() {
      this.handlers = [];

      if (this.querySelector('.complementary-products__inner')) {
        // Server rendered the cards already (e.g. fetched directly via
        // Section Rendering API into a hot-loaded page). Just bind.
        this.bindAll();
      } else {
        this.setupObserver();
      }
    }

    disconnectedCallback() {
      this.cleanupHandlers();
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    cleanupHandlers() {
      this.handlers.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
      this.handlers = [];
    }

    on(el, type, fn) {
      el.addEventListener(type, fn);
      this.handlers.push({ el, type, fn });
    }

    setupObserver() {
      this.observer = new IntersectionObserver((entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.loadRecommendations();
      }, { rootMargin: '0px 0px 400px 0px' });
      this.observer.observe(this);
    }

    async loadRecommendations() {
      try {
        const requestedCount = this.getRequestedCount();
        const complementaryInner = await this.fetchRecommendationsInner(this.dataset.url);
        const relatedUrl = this.buildRecommendationsUrl('related');

        let relatedInner = null;
        let finalInner = complementaryInner;

        if (!finalInner && relatedUrl) {
          // No curated complementary picks: fall back to related products.
          relatedInner = await this.fetchRecommendationsInner(relatedUrl);
          finalInner = relatedInner;
        }

        if (!finalInner) {
          this.remove();
          return;
        }

        if (this.countCards(finalInner) < requestedCount && relatedUrl) {
          if (!relatedInner) {
            relatedInner = await this.fetchRecommendationsInner(relatedUrl);
          }
          if (relatedInner && relatedInner !== finalInner) {
            this.appendUniqueCards(finalInner, relatedInner, requestedCount);
          }
        }

        if (this.countCards(finalInner) === 0) {
          this.remove();
          return;
        }

        this.applyHeading(finalInner);

        this.innerHTML = finalInner.outerHTML;
        this.bindAll();
      } catch (err) {
        console.error('complementary-products: failed to load recommendations', err);
        this.remove();
      }
    }

    getRequestedCount() {
      try {
        const url = new URL(this.dataset.url, window.location.origin);
        const limit = parseInt(url.searchParams.get('limit'), 10);
        if (Number.isFinite(limit) && limit > 0) return limit;
      } catch (err) {
        // Ignore malformed URLs and fall back to default.
      }
      return 4;
    }

    buildRecommendationsUrl(intent) {
      try {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('intent', intent);
        return url.toString();
      } catch (err) {
        return null;
      }
    }

    async fetchRecommendationsInner(url) {
      if (!url) return null;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('HTTP ' + response.status);

        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        // Section Rendering API usually returns plain section markup
        // (no <complementary-products> wrapper), so support both shapes.
        const fetched = doc.querySelector('complementary-products');
        return fetched
          ? fetched.querySelector('.complementary-products__inner')
          : doc.querySelector('.complementary-products__inner');
      } catch (err) {
        console.warn('complementary-products: fetch failed for', url, err);
        return null;
      }
    }

    countCards(inner) {
      if (!inner) return 0;
      return inner.querySelectorAll('.complementary-products__card').length;
    }

    applyHeading(inner) {
      if (!inner) return;

      const configuredHeading = (this.dataset.heading || '').trim();
      const existingHeading = inner.querySelector('.complementary-products__title');

      if (!configuredHeading) {
        if (existingHeading) existingHeading.remove();
        return;
      }

      if (existingHeading) {
        existingHeading.textContent = configuredHeading;
        return;
      }

      const heading = document.createElement('h2');
      heading.className = 'complementary-products__title';
      heading.textContent = configuredHeading;
      inner.prepend(heading);
    }

    appendUniqueCards(targetInner, sourceInner, maxCount) {
      const targetTrack = targetInner.querySelector('.complementary-products__track');
      const sourceCards = sourceInner.querySelectorAll('.complementary-products__card');
      if (!targetTrack || !sourceCards.length) return;

      const existingIds = new Set();
      targetTrack.querySelectorAll('.complementary-products__card').forEach((card) => {
        if (card.dataset.productId) existingIds.add(card.dataset.productId);
      });

      let currentCount = this.countCards(targetInner);
      for (let i = 0; i < sourceCards.length; i += 1) {
        if (currentCount >= maxCount) break;

        const sourceCard = sourceCards[i];
        const sourceId = sourceCard.dataset.productId;
        if (sourceId && existingIds.has(sourceId)) continue;

        if (sourceId) existingIds.add(sourceId);
        targetTrack.appendChild(sourceCard.cloneNode(true));
        currentCount += 1;
      }
    }

    bindAll() {
      this.bindCards();
      this.bindCarousel();
    }

    bindCards() {
      this.querySelectorAll('.complementary-products__card').forEach((card) => {
        card.querySelectorAll('.complementary-products__variant').forEach((btn) => {
          this.on(btn, 'click', () => this.selectVariant(card, btn));
        });

        const atc = card.querySelector('.complementary-products__atc');
        if (atc) this.on(atc, 'click', () => this.addToCart(card, atc));
      });
    }

    selectVariant(card, btn) {
      if (btn.disabled) return;

      card.querySelectorAll('.complementary-products__variant').forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-checked', 'true');

      const atc = card.querySelector('.complementary-products__atc');
      if (atc) {
        atc.dataset.variantId = btn.dataset.variantId;
        const available = btn.dataset.available === 'true';
        atc.disabled = !available;
        const text = atc.querySelector('.text');
        if (text) {
          text.textContent = available
            ? this.translate('product_add_to_cart', 'Add to cart')
            : this.translate('product_sold_out', 'Sold out');
        }
      }

      this.updatePrice(card, btn);
    }

    updatePrice(card, btn) {
      const price = parseInt(btn.dataset.price, 10);
      const compareAt = parseInt(btn.dataset.compareAtPrice, 10);
      const currentEl = card.querySelector('.current-price');
      const wasEl = card.querySelector('.was-price');
      const onSale = compareAt > price;

      if (currentEl) {
        currentEl.innerHTML = this.priceHtml(price, onSale ? this.translate('sale_price', 'Sale price') : '');
        currentEl.classList.toggle('sale', onSale);
      }
      if (wasEl) {
        if (onSale) {
          wasEl.innerHTML = this.priceHtml(compareAt, this.translate('regular_price', 'Regular price'));
          wasEl.hidden = false;
        } else {
          wasEl.hidden = true;
        }
      }
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    priceHtml(cents, label) {
      const money = this.formatMoney(cents);
      if (!label) return money;
      return '<span class="visuallyhidden">' + label + '</span>' + money;
    }

    translate(key, fallback) {
      return (window.Shopify && window.Shopify.translation && window.Shopify.translation[key]) || fallback;
    }

    async addToCart(card, atc) {
      if (atc.disabled) return;
      const variantId = atc.dataset.variantId;
      if (!variantId) return;

      const textEl = atc.querySelector('.text');
      const originalText = textEl ? textEl.textContent : '';
      atc.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.description || ('HTTP ' + response.status));
        }

        if (window.CartDrawer && typeof window.CartDrawer.fetchCart === 'function' && typeof window.CartDrawer.open === 'function') {
          window.CartDrawer.fetchCart();
          window.CartDrawer.open();
        } else {
          window.location.href = '/cart';
        }
        atc.disabled = false;
      } catch (err) {
        console.error('complementary-products: add to cart failed', err);
        if (textEl) textEl.textContent = this.translate('product_unavailable', 'Unavailable');
        setTimeout(() => {
          if (textEl) textEl.textContent = originalText;
          atc.disabled = false;
        }, 1500);
      }
    }

    bindCarousel() {
      const track = this.querySelector('.complementary-products__track');
      const prev = this.querySelector('.complementary-products__arrow--prev');
      const next = this.querySelector('.complementary-products__arrow--next');
      if (!track || !prev || !next) return;

      const getCards = () => Array.from(track.querySelectorAll('.complementary-products__card'));

      const getActiveIndex = (cards) => {
        if (!cards.length) return -1;

        const trackCenter = track.scrollLeft + (track.clientWidth / 2);
        let closestIndex = 0;
        let closestDistance = Infinity;

        cards.forEach((card, index) => {
          const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
          const distance = Math.abs(cardCenter - trackCenter);
          if (distance < closestDistance) {
            closestIndex = index;
            closestDistance = distance;
          }
        });

        return closestIndex;
      };

      const scrollToIndex = (index, behavior) => {
        const cards = getCards();
        if (!cards.length) return;

        const cardCount = cards.length;
        const normalizedIndex = ((index % cardCount) + cardCount) % cardCount;
        track.scrollTo({ left: cards[normalizedIndex].offsetLeft, behavior });
      };

      const step = (direction) => {
        const cards = getCards();
        if (cards.length <= 1) return;

        const currentIndex = getActiveIndex(cards);
        if (currentIndex < 0) return;

        const nextIndex = (currentIndex + direction + cards.length) % cards.length;
        scrollToIndex(nextIndex, 'smooth');
      };

      const prefersReducedMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const autoplayDelay = 5000;

      const stopAutoplay = () => {
        if (!this.autoplayTimer) return;
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      };

      const startAutoplay = () => {
        stopAutoplay();

        const cards = getCards();
        if (prefersReducedMotion || cards.length <= 1) return;

        this.autoplayTimer = window.setInterval(() => {
          step(1);
        }, autoplayDelay);
      };

      const restartAutoplay = () => {
        startAutoplay();
      };

      this.on(prev, 'click', () => {
        step(-1);
        restartAutoplay();
      });
      this.on(next, 'click', () => {
        step(1);
        restartAutoplay();
      });

      const getActiveCard = () => {
        const cards = getCards();
        if (!cards.length) return null;

        const activeIndex = getActiveIndex(cards);
        if (activeIndex < 0) return null;
        return cards[activeIndex];
      };

      const updateCarouselLayout = () => {
        const cards = getCards();
        const hasMultipleCards = cards.length > 1;
        prev.classList.toggle('is-hidden', !hasMultipleCards);
        next.classList.toggle('is-hidden', !hasMultipleCards);

        // Keep arrows vertically centered to the currently active card,
        // even when cards have different content heights.
        const activeCard = getActiveCard();
        if (activeCard) {
          track.style.height = Math.ceil(activeCard.getBoundingClientRect().height) + 'px';
        }
      };

      this.on(track, 'scroll', updateCarouselLayout);
      this.on(window, 'resize', updateCarouselLayout);
      this.on(track, 'mouseenter', stopAutoplay);
      this.on(track, 'mouseleave', startAutoplay);
      this.on(track, 'focusin', stopAutoplay);
      this.on(track, 'focusout', (evt) => {
        if (!this.contains(evt.relatedTarget)) startAutoplay();
      });
      this.on(document, 'visibilitychange', () => {
        if (document.hidden) {
          stopAutoplay();
        } else {
          startAutoplay();
        }
      });

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(updateCarouselLayout);
        this.resizeObserver.observe(track);
      }

      window.requestAnimationFrame(updateCarouselLayout);
      startAutoplay();
    }
  });
}

/*
  Globo bridge for "Looks Cute With".
  Globo injects markup into .globo_widget_19918; this adapter applies the
  section heading override and converts variant <select> controls into the
  chip-button UI used by complementary-products.
*/
(() => {
  const WIDGET_SELECTOR = '.globo_widget_19918.complementary-products--globo';
  const BRIDGE_CLASS = 'complementary-products--globo';
  const SOLD_OUT_SUFFIX = /\s*-\s*sold out\s*$/i;

  const forceSingleItemCarouselConfig = () => {
    const cfg = window.globoRelatedProductsConfig;
    if (!cfg) return;

    const setSingle = (carouselCfg) => {
      if (!carouselCfg) return;
      carouselCfg.large = '1';
      carouselCfg.small = '1';
      carouselCfg.tablet = '1';
      carouselCfg.mobile = '1';
    };

    if (cfg.settings) {
      cfg.settings.carousel_items = '1';
    }

    if (cfg.productRelated && cfg.productRelated.template) {
      setSingle(cfg.productRelated.template.customCarousel);
    }

    if (Array.isArray(cfg.widgets)) {
      cfg.widgets.forEach((widget) => {
        if (!widget || String(widget.id) !== '19918') return;
        if (widget.template) setSingle(widget.template.customCarousel);
      });
    }

    if (cfg.layouts) {
      Object.keys(cfg.layouts).forEach((layoutId) => {
        const layout = cfg.layouts[layoutId];
        if (layout && layout.template) {
          setSingle(layout.template.customCarousel);
        }
      });
    }
  };

  const restorePatchedCarouselConfig = () => {
    const cfg = window.globoRelatedProductsConfig;
    const backup = window.__complementaryGloboBridgeConfigBackup;
    if (!cfg || !backup) return;

    const restoreCarousel = (target, source) => {
      if (!target || !source) return;

      ['large', 'small', 'tablet', 'mobile'].forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      });
    };

    if (
      cfg.settings &&
      backup.settings &&
      Object.prototype.hasOwnProperty.call(backup.settings, 'carousel_items')
    ) {
      cfg.settings.carousel_items = backup.settings.carousel_items;
    }

    if (cfg.productRelated && cfg.productRelated.template) {
      restoreCarousel(cfg.productRelated.template.customCarousel, backup.productRelated);
    }

    if (Array.isArray(cfg.widgets) && backup.widgets) {
      cfg.widgets.forEach((widget) => {
        if (!widget || !widget.template) return;
        const saved = backup.widgets[String(widget.id)];
        restoreCarousel(widget.template.customCarousel, saved);
      });
    }

    if (cfg.layouts && backup.layouts) {
      Object.keys(cfg.layouts).forEach((layoutId) => {
        const layout = cfg.layouts[layoutId];
        if (!layout || !layout.template) return;
        restoreCarousel(layout.template.customCarousel, backup.layouts[layoutId]);
      });
    }
  };

  const resetEnhancedAddToCartButton = (button) => {
    if (!button) return;

    const clone = button.cloneNode(true);
    clone.removeAttribute('data-complementary-enhanced');
    clone.removeAttribute('data-complementary-loading');
    clone.disabled = false;

    const textEl = clone.querySelector('.text');
    if (textEl && clone.childElementCount === 1) {
      clone.textContent = textEl.textContent.trim();
    }

    button.replaceWith(clone);
  };

  const disableBridge = (widget, reason, observer) => {
    if (!widget || widget.dataset.complementaryBridgeDisabled === 'true') return;

    if (observer && typeof observer.disconnect === 'function') {
      observer.disconnect();
    }

    widget.dataset.complementaryBridgeDisabled = 'true';
    widget.dataset.complementaryBridgeDisabledReason = reason || 'unknown';

    // Restore Globo config values previously patched by this bridge layer.
    restorePatchedCarouselConfig();

    widget.querySelectorAll('.complementary-products__variants').forEach((node) => node.remove());

    widget
      .querySelectorAll('.ga-product_variant_select[data-complementary-enhanced="true"]')
      .forEach((select) => {
        select.removeAttribute('data-complementary-enhanced');
      });

    widget
      .querySelectorAll('.ga-product_addtocart[data-complementary-enhanced="true"]')
      .forEach((button) => {
        resetEnhancedAddToCartButton(button);
      });

    // Remove only the bridge class so Globo can render with app-default styles.
    widget.classList.remove(BRIDGE_CLASS);

    console.warn('complementary-products globo bridge disabled:', reason || 'contract mismatch');
  };

  const getBridgeContractState = (widget) => {
    const box = widget.querySelector('.ga-products-box');
    if (!box) return 'pending';

    const cards = Array.from(box.querySelectorAll('.ga-product'));
    if (!cards.length) return 'pending';

    const hasAddToCart = cards.some((card) => !!card.querySelector('.ga-product_addtocart'));
    const hasTitle = cards.some((card) => !!card.querySelector('.ga-product_title'));

    // Treat only hard contract mismatches as invalid. Everything else should
    // stay pending/valid so we don't disable custom rendering too early.
    if (!hasAddToCart || !hasTitle) return 'invalid';

    return 'valid';
  };

  const translate = (key, fallback) => {
    if (window.Shopify && window.Shopify.translation && window.Shopify.translation[key]) {
      return window.Shopify.translation[key];
    }
    return fallback;
  };

  const addToCartAjax = async (variantId) => {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.description || ('HTTP ' + response.status));
    }

    return response.json().catch(() => ({}));
  };

  const openThemeCart = () => {
    if (window.CartDrawer && typeof window.CartDrawer.fetchCart === 'function') {
      window.CartDrawer.fetchCart();
      if (typeof window.CartDrawer.open === 'function') {
        window.CartDrawer.open();
      }
      return;
    }

    window.location.href = '/cart';
  };

  const normalizeOptionLabel = (text) => (text || '').replace(SOLD_OUT_SUFFIX, '').trim();

  const syncVariantButtons = (select, buttonWrap) => {
    const options = Array.from(select.options);
    const buttons = Array.from(buttonWrap.querySelectorAll('.complementary-products__variant'));

    options.forEach((option, index) => {
      const button = buttons[index];
      if (!button) return;

      const selected = option.selected;
      button.disabled = option.disabled;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
    });
  };

  const isDefaultTitleOption = (option) =>
    /^default title$/i.test(normalizeOptionLabel(option.textContent));

  const enhanceVariantSelect = (select) => {
    if (select.dataset.complementaryEnhanced === 'true') return;

    const options = Array.from(select.options);
    if (!options.length) {
      select.dataset.complementaryEnhanced = 'true';
      return;
    }

    // Products with no real variants get Shopify's auto-generated single
    // "Default Title" variant. There's no choice to make and the product name
    // already shows as the card title, so skip the chip entirely. ATC still
    // works because it reads the selected variant from the hidden <select>.
    if (options.length === 1 && isDefaultTitleOption(options[0])) {
      const card = select.closest('.ga-product');
      if (card) card.classList.add('is-single-variant');
      select.dataset.complementaryEnhanced = 'true';
      return;
    }

    const variantWrap = document.createElement('div');
    variantWrap.className = 'complementary-products__variants';
    variantWrap.setAttribute('role', 'radiogroup');
    variantWrap.setAttribute('aria-label', select.getAttribute('aria-label') || 'Options');

    options.forEach((option, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'complementary-products__variant';
      button.textContent = normalizeOptionLabel(option.textContent);
      button.setAttribute('role', 'radio');

      button.addEventListener('click', () => {
        if (option.disabled) return;
        select.selectedIndex = index;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        syncVariantButtons(select, variantWrap);
      });

      variantWrap.appendChild(button);
    });

    select.insertAdjacentElement('afterend', variantWrap);
    select.addEventListener('change', () => syncVariantButtons(select, variantWrap));

    syncVariantButtons(select, variantWrap);
    select.dataset.complementaryEnhanced = 'true';
  };

  const enhanceAddToCart = (addToCart) => {
    if (!addToCart || addToCart.dataset.complementaryEnhanced === 'true') return;

    const card = addToCart.closest('.ga-product');
    const cardSelect = card ? card.querySelector('.ga-product_variant_select') : null;

    // If Globo no longer exposes a variant select on this card, leave the
    // native app behavior untouched for that button.
    if (!cardSelect) return;

    if (!addToCart.querySelector('.text')) {
      const text = document.createElement('span');
      text.className = 'text';
      text.textContent = addToCart.textContent.trim();
      addToCart.textContent = '';
      addToCart.appendChild(text);
    }

    const textEl = addToCart.querySelector('.text');

    addToCart.addEventListener('click', async (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      if (typeof evt.stopImmediatePropagation === 'function') {
        evt.stopImmediatePropagation();
      }

      if (addToCart.dataset.complementaryLoading === 'true') return;

      const card = addToCart.closest('.ga-product');
      const select = card ? card.querySelector('.ga-product_variant_select') : null;
      const selectedOption =
        select && select.options && select.selectedIndex >= 0
          ? select.options[select.selectedIndex]
          : null;

      const variantId = selectedOption ? selectedOption.value : null;
      const isAvailable = selectedOption
        ? selectedOption.dataset.available !== 'false' && !selectedOption.disabled
        : true;

      if (!variantId) return;

      if (!isAvailable) {
        addToCart.disabled = true;
        if (textEl) textEl.textContent = translate('product_sold_out', 'Sold out');
        return;
      }

      const originalText = textEl ? textEl.textContent : '';
      const addedText = addToCart.getAttribute('data-added') || 'Added to cart';

      addToCart.dataset.complementaryLoading = 'true';
      addToCart.disabled = true;

      try {
        await addToCartAjax(variantId);
        if (textEl) textEl.textContent = addedText;
        openThemeCart();

        window.setTimeout(() => {
          if (textEl) textEl.textContent = originalText;
          addToCart.disabled = false;
          delete addToCart.dataset.complementaryLoading;
        }, 700);
      } catch (err) {
        console.error('complementary-products globo add to cart failed', err);

        if (textEl) {
          textEl.textContent = translate('product_unavailable', 'Unavailable');
        }

        window.setTimeout(() => {
          if (textEl) textEl.textContent = originalText;
          addToCart.disabled = false;
          delete addToCart.dataset.complementaryLoading;
        }, 1200);
      }
    }, true);

    addToCart.dataset.complementaryEnhanced = 'true';
  };

  // Adaptive height: gowl forces every product card to the height of the
  // tallest one, so the in-view card leaves white space below it. Clamp the
  // carousel viewport to the actual content height of whichever card is shown.
  //
  // gowl builds — and sometimes rebuilds — the carousel after we enhance the
  // markup, so instead of binding once we keep a persistent observer on the
  // widget and re-resolve the stage on every mutation. The rAF debounce keeps
  // it cheap, and skipping no-op writes stops the observer looping on our own
  // height change.
  const setupHeightSync = (widget) => {
    if (widget.dataset.complementaryHeightSync === 'true') return;
    widget.dataset.complementaryHeightSync = 'true';

    const getActiveItem = (stage) => {
      const items = Array.from(stage.children).filter(
        (node) => node.classList && node.classList.contains('gowl-item'),
      );
      if (!items.length) return null;

      // gowl slides by translating the stage; the destination transform,
      // matched against each item's static layout position, tells us which
      // card lands in the single-card viewport. offsetLeft is unaffected by
      // the transform (and by gowl's clone reordering), so this reads
      // correctly even mid-animation.
      const base = items[0].offsetLeft;
      const match = /translate(?:3d)?\(\s*(-?[\d.]+)px/.exec(stage.style.transform || '');
      const offset = match ? Math.abs(parseFloat(match[1])) : 0;

      let best = items[0];
      let bestDist = Infinity;
      items.forEach((item) => {
        const dist = Math.abs(item.offsetLeft - base - offset);
        if (dist < bestDist) {
          bestDist = dist;
          best = item;
        }
      });
      return best;
    };

    let raf = null;
    const syncHeight = () => {
      raf = null;

      const stage = widget.querySelector('.gowl-stage');
      const stageOuter = widget.querySelector('.gowl-stage-outer');
      if (!stage || !stageOuter) return;

      const item = getActiveItem(stage);
      if (!item) return;

      // Measure the card content (.ga-product), not the .gowl-item wrapper:
      // gowl pads the wrapper out to the tallest card, and that padding is
      // exactly the white space we want to crop.
      const card = item.querySelector('.ga-product') || item;
      const cardCs = window.getComputedStyle(card);
      const used =
        card.getBoundingClientRect().bottom -
        item.getBoundingClientRect().top +
        (parseFloat(cardCs.marginBottom) || 0);
      if (used <= 0) return;

      const outerCs = window.getComputedStyle(stageOuter);
      const padV = (parseFloat(outerCs.paddingTop) || 0) + (parseFloat(outerCs.paddingBottom) || 0);

      const value = Math.ceil(used + padV) + 'px';
      if (stageOuter.style.height === value) return; // no-op; avoids observer loop
      stageOuter.style.setProperty('height', value, 'important');
    };

    const scheduleSync = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(syncHeight);
    };

    // gowl rebuilds the stage and rewrites stage.style.transform on each slide;
    // watch the whole widget so we re-clamp through both.
    const observer = new MutationObserver(scheduleSync);
    observer.observe(widget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });
    window.addEventListener('resize', scheduleSync);

    // Initial measures, plus a couple after gowl + images/fonts settle.
    scheduleSync();
    window.setTimeout(scheduleSync, 400);
    window.setTimeout(scheduleSync, 1200);
  };

  // Autoplay: auto-advance the gowl carousel every 5s by triggering the
  // next arrow. Pauses on hover/focus/hidden-tab. Disabled for reduced-motion.
  const setupAutoplay = (widget) => {
    if (widget.dataset.complementaryAutoplay === 'true') return;
    widget.dataset.complementaryAutoplay = 'true';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const AUTOPLAY_DELAY = 5000;
    let timer = null;

    const getNextBtn = () => widget.querySelector('.gowl-next');

    const advance = () => {
      const btn = getNextBtn();
      if (btn) btn.click();
    };

    const stop = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      if (!getNextBtn()) return;
      timer = window.setInterval(advance, AUTOPLAY_DELAY);
    };

    // Pause on hover / focus-within / hidden tab
    widget.addEventListener('mouseenter', stop);
    widget.addEventListener('mouseleave', start);
    widget.addEventListener('focusin', stop);
    widget.addEventListener('focusout', (evt) => {
      if (!widget.contains(evt.relatedTarget)) start();
    });

    const handleVisibilityChange = () => {
      if (!document.body.contains(widget)) {
        stop();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        return;
      }
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Kick off after a short settle so gowl has finished initializing
    window.setTimeout(start, 1000);
  };

  const decorateWidget = (widget) => {
    const box = widget.querySelector('.ga-products-box');
    if (!box) return;

    widget.querySelectorAll('.ga-product_addtocart').forEach(enhanceAddToCart);
    widget
      .querySelectorAll('.ga-product_variant_select:not([data-complementary-enhanced="true"])')
      .forEach(enhanceVariantSelect);

    setupHeightSync(widget);
    setupAutoplay(widget);
  };

  const observeWidget = (widget) => {
    if (widget.dataset.complementaryObserverBound === 'true') return;
    widget.dataset.complementaryObserverBound = 'true';

    let scheduled = false;
    let observer;
    let configPatched = false;
    let invalidCount = 0;
    const startedAt = Date.now();
    const invalidGraceMs = 6000;

    const maybeDisconnect = () => {
      if (!observer) return;

      if (widget.dataset.complementaryBridgeDisabled === 'true') {
        observer.disconnect();
        return;
      }

      const hasBox = !!widget.querySelector('.ga-products-box');
      const pendingSelects = widget.querySelectorAll(
        '.ga-product_variant_select:not([data-complementary-enhanced="true"])',
      ).length;

      if (hasBox && pendingSelects === 0) {
        observer.disconnect();
      }
    };

    const runDecorate = () => {
      scheduled = false;

      if (widget.dataset.complementaryBridgeDisabled === 'true') {
        if (observer) observer.disconnect();
        return;
      }

      const contractState = getBridgeContractState(widget);

      if (contractState === 'invalid') {
        invalidCount += 1;

        // Globo can mutate through intermediate states while hydrating.
        // Only disable after repeated hard-invalid checks and a grace window.
        if (Date.now() - startedAt > invalidGraceMs && invalidCount >= 3) {
          disableBridge(widget, 'missing required Globo selectors', observer);
        }
        return;
      }

      if (contractState === 'pending') {
        return;
      }

      invalidCount = 0;

      if (!configPatched) {
        forceSingleItemCarouselConfig();
        configPatched = true;
      }

      decorateWidget(widget);
      maybeDisconnect();
    };

    const scheduleDecorate = () => {
      if (scheduled) return;
      scheduled = true;

      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(runDecorate);
      } else {
        window.setTimeout(runDecorate, 16);
      }
    };

    observer = new MutationObserver(scheduleDecorate);
    observer.observe(widget, { childList: true, subtree: true });

    // Globo may mutate while hydrating/carousel bootstrapping; after that,
    // disconnect to avoid long-running observer overhead on product pages.
    window.setTimeout(() => observer.disconnect(), 12000);

    runDecorate();
  };

  const init = () => {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(observeWidget);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
